import { Box, Fade, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { Body } from './Body';
import { NavigationDrawer } from './NavigationDrawer';
import { useNavigate } from 'react-router';
import { PageAccessMap } from 'src/Menus';
import { ApiSdkEvents, TaskCollection, TaskState } from '@ekkogmbh/apisdk';
import ObjectField from './DataTable/ObjectField';
import { snackbarActionStack } from './Notifications';
import { EngineeringOutlined } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import useEkko from '../Providers/EkkoProvider';
import { useSearchBar } from '../Providers/SearchBarProvider';
import { DataTableProvider } from '../Providers/DataTableProvider';
import useStableCallback from '../Hooks/useStableCallback';

interface PageProps {
  title: string;
  accessKey?: string;
  isSearchable?: boolean;
  children: React.ReactNode;
}

export const Page = ({ title, accessKey, isSearchable, children }: PageProps): React.JSX.Element => {
  const { isAuthenticated, hasPermission, eventSubscribe, eventUnsubscribe } = useEkko();
  const { setHidden: hideSearchBar } = useSearchBar();
  const navigate = useNavigate();

  /** on mount */
  useEffect(() => {
    console.log('[UI]: entering page: ' + title);

    const permission = PageAccessMap[accessKey!]?.permission;
    const isPermitted = permission ? hasPermission(permission) : true;

    if (!isPermitted) {
      console.log('[EKKO] missing permission:', permission);
      navigate('/');
    }
  }, []);

  useEffect(() => {
    hideSearchBar(isSearchable !== true);
  }, [isSearchable]);

  // this is defined here because we want these notifications on every page
  // and also be able to use the navigate function.
  const emitTaskCollectionNotification = useStableCallback((taskCollection: TaskCollection) => {
    enqueueSnackbar(
      <Stack direction={'column'}>
        <Typography fontWeight={700}>Tasks Completed</Typography>
        <ObjectField
          direction={'column'}
          fields={[
            { key: 'collection-id', value: <Typography color={'primary'}>{taskCollection.id}</Typography> },
            {
              key: 'state',
              value: (
                <Typography color={taskCollection.state === TaskState.FAILED ? 'error' : 'primary'}>
                  {taskCollection.state}
                </Typography>
              ),
            },
          ]}
        />
      </Stack>,
      {
        action: (snackKey) =>
          snackbarActionStack(snackKey, [
            {
              label: 'go to tasks',
              icon: EngineeringOutlined,
              dismiss: true,
              onClick: () => navigate('/tasks/collection/' + taskCollection.id),
            },
          ]),
      },
    );
  }, []);

  /** event subscriptions */
  useEffect(() => {
    eventSubscribe(ApiSdkEvents.TASK_COLLECTION_FAILED, emitTaskCollectionNotification);
    eventSubscribe(ApiSdkEvents.TASK_COLLECTION_FINISHED, emitTaskCollectionNotification);
    return () => {
      eventUnsubscribe(ApiSdkEvents.TASK_COLLECTION_FAILED, emitTaskCollectionNotification);
      eventUnsubscribe(ApiSdkEvents.TASK_COLLECTION_FINISHED, emitTaskCollectionNotification);
    };
  }, [emitTaskCollectionNotification]);

  /** go to login when no longer authed */
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('[UI] redirect to login');
      navigate('/login');
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <>{'something went wrong'}</>;
  }

  return (
    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
      <NavigationDrawer />
      <DataTableProvider>
        <Body key={`body-${title}`} title={title} sx={{ width: '100%' }}>
          <Fade in={true} timeout={350}>
            <Box component={'div'}>{children}</Box>
          </Fade>
        </Body>
      </DataTableProvider>
    </Stack>
  );
};
